import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import HeroSection from './components/HeroSection';
import HotRanking from './components/HotRanking';
import FeaturedEvents from './components/FeaturedEvents';
import TopicGrid from './components/TopicGrid';
import DigestSection from './components/DigestSection';
import TestimonialSection from './components/TestimonialSection';
import FaqSection from './components/FaqSection';
import SubscribeSection from './components/SubscribeSection';
import { useSeo } from '@/hooks/useSeo';
import { hotTopics, faqs, siteInfo } from '@/mocks/site';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Home() {
  const location = useLocation();

  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__.replace(/\/$/, '') : '';
    const siteUrl = `${origin}${basePath}`;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: siteInfo.name,
        alternateName: '17c吃瓜热点速递',
        url: `${siteUrl}/`,
        description: siteInfo.description,
        inLanguage: 'zh-CN',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        url: `${siteUrl}/`,
        description: siteInfo.description,
        email: siteInfo.email,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: '今日热度排行',
        itemListElement: hotTopics.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `${siteUrl}/article/${item.slug}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ];
  }, []);

  useSeo({
    title: '17c吃瓜 - 今日热门事件实时更新 | 最新爆料全网热议速递',
    description:
      '17c吃瓜聚焦全网热议话题，实时更新今日热门事件，深挖最新爆料内幕，梳理事件脉络、关键细节与网友焦点，一手瓜源持续刷新，满足你对今日热点的全部好奇。',
    keywords: ['17c吃瓜', '今日热门事件', '最新爆料', '全网热议', '实时更新', '热门话题'],
    path: '/',
    jsonLd,
  });

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
  }, [location.hash, location.pathname]);

  const sections = [
    { id: 'hot', label: '热榜' },
    { id: 'events', label: '事件' },
    { id: 'topics', label: '分类' },
    { id: 'digest', label: '爆料' },
    { id: 'faq', label: '问答' },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <nav
          aria-label="页内导航"
          className="sticky top-16 z-40 w-full bg-background-50/95 backdrop-blur border-b border-background-200"
        >
          <div className="w-full px-4 md:px-8">
            <ul className="flex items-center gap-1 overflow-x-auto py-2">
              {sections.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="block px-3 py-1.5 text-xs md:text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <HeroSection />
        <HotRanking />
        <FeaturedEvents />
        <TopicGrid />
        <DigestSection />
        <TestimonialSection />
        <FaqSection />
        <SubscribeSection />

        <section className="w-full bg-background-100 py-10 md:py-12">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
              站内热词索引
            </h2>
            <div className="flex flex-wrap gap-2">
              {[...categories.flatMap((c) => c.keywords), ...articles.slice(0, 6).map((a) => a.tags[0])].map(
                (kw) => (
                  <span
                    key={kw}
                    className="px-3 py-1.5 text-xs rounded-md bg-background-50 border border-background-200 text-foreground-600"
                  >
                    {kw}
                  </span>
                ),
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}